/*
 * SHRUBS, STOOD UP. Asked for on 2026-10-01: "render shrubs oblique".
 *
 * A shrub is the hedge tile (Tile.Hedge): Hobbiton's hedges along the lanes
 * and round the Party Field. Flat, it is the picture tileSprite draws — a
 * dark rounded mass on grass, seen from above — and the flat views (the
 * map, the zoomed-out world) keep that picture.
 *
 * In the oblique view it stands up on its tile as a bush SHRUB_H storeys
 * tall, the way a tree does over the grass it grows in: its foot a rounded
 * patch inside its own tile, its top the same patch lifted SHRUB_H storeys,
 * which on the glass is up and to the left by that many STOREY pixels, and
 * between them its body, built as layers from the foot to the top, darker
 * low down and lit on top, with leaves on the top. The grass under it is
 * laid with the ground. It reaches over the tile north of it on the glass,
 * so whoever stands there is behind it (shrubCoversNorth, tallFront in
 * render.ts).
 *
 * It is kept in a file of its own, as the chest is, so the geometry can be
 * run by the tests without a canvas.
 */
import { PIECE_PAD, STOREY } from "./oblique";
import { TILE } from "./sprites";
import { keptContext } from "./paper";

/** How tall a shrub stands, in storeys. */
export const SHRUB_H = 0.7;
/** Its foot on its tile: west and north edges, width and height, in the tile's own pixels. */
const FOOT = { x: 2, y: 5, w: 28, h: 24 };
/** How many layers its body is built of, foot to top. */
const LAYERS = 8;

/** One layer of the bush: its patch, how far it is lifted, and how much shadow is on it (0 lit, 1 darkest). */
export interface ShrubLayer {
  x: number;
  y: number;
  w: number;
  h: number;
  lift: number;
  shade: number;
}

/**
 * The bush's layers, foot first: each the foot's patch lifted by its share
 * of the height, the body narrowing a little towards the top so it is
 * rounded rather than a box, and darkening down towards the ground.
 */
export function shrubLayers(): ShrubLayer[] {
  const out: ShrubLayer[] = [];
  const top = SHRUB_H * STOREY;
  for (let i = 0; i <= LAYERS; i++) {
    const f = i / LAYERS;
    const lift = top * f;
    // Fullest a third of the way up, pulled in at the foot and the top.
    const pull = i === 0 || i === LAYERS ? 0 : Math.round(1.5 * Math.abs(f - 0.35) * 2) / 2;
    out.push({ x: FOOT.x - lift + pull, y: FOOT.y - lift + pull, w: FOOT.w - 2 * pull, h: FOOT.h - 2 * pull, lift, shade: 1 - f });
  }
  // The top is the foot's patch exactly, lifted: the leaves are laid on it.
  out[LAYERS] = { x: FOOT.x - top, y: FOOT.y - top, w: FOOT.w, h: FOOT.h, lift: top, shade: 0 };
  return out;
}

/** Whether a shrub reaches over the tile north of it on the glass. */
export function shrubCoversNorth(): boolean {
  return shrubLayers().some((l) => l.y < 0);
}

/** The leaves' colours, dark to lit: the hedge's own, as its flat picture has them. */
const LEAF = { dark: [0x1d, 0x4a, 0x17], lit: [0x3a, 0x7d, 0x2e] };
const shrubCache = new Map<number, HTMLCanvasElement>();

/**
 * The standing shrub's picture for a variant, made once and kept:
 * PIECE_PAD bigger than the tile on every side, like the chest's, since it
 * leans up and to the left out of its square. The variant scatters its
 * leaves, so a row of hedge is not one bush repeated.
 */
export function shrubPicture(variant: number): HTMLCanvasElement {
  const v = variant & 3;
  const cached = shrubCache.get(v);
  if (cached) return cached;
  const c = document.createElement("canvas");
  c.width = c.height = TILE + 2 * PIECE_PAD;
  const ctx = keptContext(c);
  ctx.translate(PIECE_PAD, PIECE_PAD);
  const mix = (shade: number) => {
    const [r, g, b] = LEAF.dark.map((d, i) => Math.round(LEAF.lit[i] + (d - LEAF.lit[i]) * shade));
    return `rgb(${r},${g},${b})`;
  };
  const layers = shrubLayers();
  for (const l of layers) {
    ctx.fillStyle = mix(l.shade);
    ctx.beginPath();
    ctx.roundRect(l.x, l.y, l.w, l.h, 9);
    ctx.fill();
  }
  // Leaves on the top, lit and dark, scattered by the variant.
  const top = layers[layers.length - 1];
  let seed = 7 + v * 131;
  const rnd = () => ((seed = (seed * 1103515245 + 12345) & 0x7fffffff) / 0x7fffffff);
  for (let i = 0; i < 30; i++) {
    ctx.fillStyle = i % 3 === 0 ? "#4f9a3a" : i % 3 === 1 ? "#2f6a26" : "#1d4a17";
    ctx.fillRect(Math.round(top.x + 3 + rnd() * (top.w - 8)), Math.round(top.y + 3 + rnd() * (top.h - 8)), 2, 2);
  }
  shrubCache.set(v, c);
  return c;
}
